import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import SampleFirmwareInstallationStatus
    from '../../../../../../types/sample-firmware-revision/SampleFirmwareInstallationStatus';
import SampleFirmwareRevisionWriteDTO
    from '../../../../../../types/sample-firmware-revision/SampleFirmwareRevisionWriteDTO';
import SampleHardwareRevision from '../../../../../../types/sample-hardware-revision/SampleHardwareRevision';
import { SampleFirmwareRevisionService } from '../../../../services/sample-firmware-revision.service';
import {
    UploadSampleFirmwareFileComponent
} from '../upload-sample-firmware-file/upload-sample-firmware-file.component';

@Component({
    selector: 'app-create-sample-firmware-revision-dialog',
    imports: [
        DialogModule,
        InputTextModule,
        ButtonModule,
        FormsModule,
        IconField,
        InputIcon,
        IftaLabelModule,
        Textarea,
        MultiSelectModule,
        UploadSampleFirmwareFileComponent,
        Select
    ],
    templateUrl: './create-sample-firmware-revision-dialog.component.html',
    styleUrl: './create-sample-firmware-revision-dialog.component.scss'
})
export class CreateSampleFirmwareRevisionDialogComponent {
    @Input() public visible: boolean = false;
    @Output() public visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() public allInstallationStatuses: SampleFirmwareInstallationStatus[] = [];
    @Input() public allAvailableSampleHardwareRevisions: SampleHardwareRevision[] = [];
    @Output() public sampleFirmwareRevisionCreated: EventEmitter<SampleFirmwareRevisionWriteDTO> = new EventEmitter<SampleFirmwareRevisionWriteDTO>();

    public sampleFirmwareRevision: SampleFirmwareRevisionWriteDTO = new SampleFirmwareRevisionWriteDTO({});

    public loading = false;

    public compatibleSampleHardwareRevisions: SampleHardwareRevision[] = [];
    public selectedStatus?: SampleFirmwareInstallationStatus;

    public selectedFile: File | null = null;
    public resetUpload = false;

    constructor(private sampleFirmwareRevisionService: SampleFirmwareRevisionService,
                private messageService: MessageService) {
    }

    /**
     * Loads compatible installation statuses upon initialisation.
     */
    private loadInstallationStatus() {
        this.selectedStatus = this.allInstallationStatuses[0];
    }

    /**
     * Enables or Disables the save button based on required fields.
     */
    get saveDisabled(): boolean {
        return !this.sampleFirmwareRevision.releaseVersion || !this.sampleFirmwareRevision.description
            || this.compatibleSampleHardwareRevisions.length == 0 || !this.selectedFile;
    }

    /**
     * Closes the create dialog box and resets the status variables.
     */
    public close() {
        this.visible = false;
        this.sampleFirmwareRevision = new SampleFirmwareRevisionWriteDTO({});
        this.selectedFile = null;
        this.resetUpload = true;
        this.compatibleSampleHardwareRevisions = [];
        this.visibleChange.emit(this.visible);
    }

    /**
     * Sets the file selected to be uploaded.
     */
    public onFileSelected(file: File | null) {
        this.selectedFile = file;
    }

    /**
     * Creates the firmware revision by sending the create request to the server.
     */
    public saveSampleFirmwareRevision() {
        if (!this.selectedFile) {
            this.messageService.add(
                { severity: 'error', summary: 'Error', detail: 'No firmware file selected', life: 3000 });
            return;
        }
        this.loading = true;
        this.loadInstallationStatus();
        this.sampleFirmwareRevision.hardwareRevisions = this.compatibleSampleHardwareRevisions;
        this.sampleFirmwareRevision.installationStatus = this.selectedStatus?.name ?? '';
        this.sampleFirmwareRevisionService.createSampleFirmwareRevision(this.sampleFirmwareRevision, this.selectedFile)
            .then(() => {
                this.sampleFirmwareRevisionCreated.emit(this.sampleFirmwareRevision);
                this.close();
            }).catch((error) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
        }).finally(() => {
            this.loading = false;
        });
    }
}
